import { useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { gsap } from 'gsap'
import { useDesktopStore, WindowId } from '@/stores/desktopStore'
import { ControlWindow } from '@/windows/ControlWindow'
import './Taskbar.css'
import { TASKBAR_ITEMS, type TaskbarItem } from '@/data/taskbar'



export function Taskbar() {
  const { windows, openWindow, closeWindow } = useDesktopStore()
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const controlRef = useRef<HTMLButtonElement>(null)
  
  const controlWindow = windows.find((w) => w.id === 'control')
  const isControlOpen = controlWindow?.isOpen && !controlWindow.isMinimized

  // GSAP hover effects for taskbar items
  useEffect(() => {
    itemRefs.current.forEach((el) => {
      if (!el) return

      const label = el.querySelector('.taskbar__item-label')
      const symbol = el.querySelector('.taskbar__item-symbol')

      const handleEnter = () => {
        gsap.to(label, {
          letterSpacing: '0.15em',
          duration: 0.3,
          ease: 'power2.out'
        })
        gsap.to(symbol, {
          rotate: 180,
          scale: 1.3,
          duration: 0.4,
          ease: 'back.out(1.7)'
        })
      }

      const handleLeave = () => {
        gsap.to(label, {
          letterSpacing: '0.05em',
          duration: 0.3,
          ease: 'power2.out'
        })
        gsap.to(symbol, {
          rotate: 0,
          scale: 1,
          duration: 0.4,
          ease: 'power2.out'
        })
      }

      el.addEventListener('mouseenter', handleEnter)
      el.addEventListener('mouseleave', handleLeave)

      return () => {
        el.removeEventListener('mouseenter', handleEnter)
        el.removeEventListener('mouseleave', handleLeave)
      }
    })
  }, [])

  // GSAP effect for control button
  useEffect(() => {
    const el = controlRef.current
    if (!el) return

    const handleEnter = () => {
      gsap.to(el, {
        rotate: 90,
        duration: 0.4,
        ease: 'back.out(1.7)'
      })
    }

    const handleLeave = () => {
      gsap.to(el, {
        rotate: 0,
        duration: 0.4,
        ease: 'power2.out'
      })
    }

    el.addEventListener('mouseenter', handleEnter)
    el.addEventListener('mouseleave', handleLeave)

    return () => {
      el.removeEventListener('mouseenter', handleEnter)
      el.removeEventListener('mouseleave', handleLeave)
    }
  }, [])

  const handleItemClick = (id: WindowId) => {
    const windowState = windows.find((w) => w.id === id)
    if (windowState?.isOpen && !windowState.isMinimized) {
      closeWindow(id)
    } else {
      openWindow(id)
    }
  }

  const handleControlClick = () => {
    if (isControlOpen) {
      closeWindow('control')
    } else {
      openWindow('control')
    }
  }

  return (
    <>
      {/* Control Panel Popup */}
      <AnimatePresence>
        {isControlOpen && (
          <motion.div
            className="control-popup"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <ControlWindow />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="taskbar"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <div className="taskbar__center">
          {TASKBAR_ITEMS.map((item, index) => {
            const windowState = windows.find((w) => w.id === item.id)
            const isActive = windowState?.isOpen && !windowState.isMinimized

            return (
              <motion.button
                key={item.id}
                ref={(el) => { itemRefs.current[index] = el }}
                className={`taskbar__item ${isActive ? 'taskbar__item--active' : ''}`}
                onClick={() => handleItemClick(item.id)}
                whileTap={{ scale: 0.95 }}
              >
                <span className="taskbar__item-symbol">{item.symbol}</span>
                <span className="taskbar__item-label">{item.label}</span>
                {isActive && <span className="taskbar__item-indicator" />}
              </motion.button>
            )
          })}
        </div>

        <motion.button
          ref={controlRef}
          className={`taskbar__control-btn ${isControlOpen ? 'taskbar__control-btn--active' : ''}`}
          onClick={handleControlClick}
          whileTap={{ scale: 0.95 }}
          title="Settings"
        >
          ✦
        </motion.button>
      </motion.div>
    </>
  )
}
